import React from "react";

const TYPE_META = {
  payment:     { icon: "fas fa-mobile-alt",      tone: "y" },
  quote:       { icon: "fas fa-file-invoice",    tone: "g" },
  review:      { icon: "fas fa-star",            tone: "y" },
  support:     { icon: "fas fa-circle-question", tone: "g" },
  job_update:  { icon: "fas fa-tools",           tone: "y" },
  appointment: { icon: "fas fa-calendar-check",  tone: "g" },
  message:     { icon: "fas fa-comment-dots",    tone: "g" },
  saved:       { icon: "fas fa-heart",           tone: "g" },
  system:      { icon: "fas fa-bell",            tone: "g" },
};
const defaultMeta = { icon: "fas fa-bell", tone: "g" };

const NTF_CSS = `
.ntf { --ink:#0c0c0b; --muted:#6b6b66; --line:#e6e6e1; --fill:#f3f3ef; --y:#fff000;
  max-width: 860px; margin: 0 auto; color: var(--ink); font-family: inherit; font-size: 14px; line-height: 1.5; }
.ntf *, .ntf *::before, .ntf *::after { box-sizing: border-box; }
.ntf :focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.ntf-card { background: #fff; border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.ntf-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.ntf-title { margin: 0; font-size: 15px; font-weight: 600; }
.ntf-sub { margin: 2px 0 0; color: var(--muted); font-size: 13px; }
.ntf-mark { display: inline-flex; align-items: center; gap: 8px; min-height: 34px; padding: 0 12px; border: 1px solid var(--line); border-radius: 8px; background: #fff; color: var(--ink); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.ntf-mark:hover { background: var(--fill); }

.ntf-item { display: flex; gap: 14px; align-items: flex-start; width: 100%; padding: 14px 20px; border: 0; background: #fff; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.ntf-item + .ntf-item { border-top: 1px solid var(--line); }
.ntf-item:hover { background: #fafaf8; }
.ntf-item.unread { background: #fffdf0; }
.ntf-item.unread:hover { background: #fffbd6; }
.ntf-icon { display: grid; place-items: center; width: 36px; height: 36px; flex: 0 0 36px; border-radius: 10px; font-size: .82rem; color: var(--ink); }
.ntf-icon.y { background: #fff7a8; }
.ntf-icon.g { background: var(--fill); }
.ntf-body { flex: 1; min-width: 0; }
.ntf-item-title { margin: 0; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.ntf-item.unread .ntf-item-title { font-weight: 700; }
.ntf-item-msg { margin: 2px 0 0; color: var(--muted); font-size: 13px; overflow-wrap: anywhere; }
.ntf-item-time { margin: 4px 0 0; color: #8a8a84; font-size: 12px; }
.ntf-dot { width: 9px; height: 9px; flex: 0 0 9px; margin-top: 7px; border-radius: 50%; background: var(--y); box-shadow: 0 0 0 1.5px var(--ink); }
.ntf-chev { align-self: center; color: #a5a59e; font-size: .75rem; }

.ntf-center { padding: 48px 20px; text-align: center; }
.ntf-spinner { display: inline-block; width: 28px; height: 28px; border: 3px solid var(--line); border-top-color: var(--ink); border-radius: 50%; animation: ntf-spin .7s linear infinite; }
@keyframes ntf-spin { to { transform: rotate(360deg); } }
.ntf-empty-icon { display: grid; place-items: center; width: 56px; height: 56px; margin: 0 auto 14px; border: 1px solid var(--line); border-radius: 50%; background: var(--fill); font-size: 1.2rem; }
.ntf-center h3 { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
.ntf-center p { max-width: 340px; margin: 0 auto; color: var(--muted); }
.ntf-retry { display: inline-flex; align-items: center; gap: 8px; margin-top: 16px; min-height: 38px; padding: 0 16px; border: 0; border-radius: 8px; background: var(--y); color: #000; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
.ntf-retry:hover { background: #ffe600; }
@media (prefers-reduced-motion: reduce) { .ntf-spinner { animation: none; } }
`;

const when = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-KE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

/**
 * Props
 * - notifications: rows from GET /notifications/ { id, type, title, message, link_tab, related_job, is_read, created_at }
 * - loading, error, unreadCount
 * - onMarkAllRead(): void
 * - onNotificationClick(n): marks read and opens the right screen (handled by the page)
 * - onRetry(): void
 */
export default function NotificationsTab({
  notifications = [],
  loading = false,
  error = "",
  unreadCount = 0,
  onMarkAllRead,
  onNotificationClick,
  onRetry,
}) {
  const list = Array.isArray(notifications) ? notifications : [];

  return (
    <div className="ntf">
      <style>{NTF_CSS}</style>

      <div className="ntf-card">
        <div className="ntf-head">
          <div>
            <h2 className="ntf-title">All notifications</h2>
            <p className="ntf-sub">{unreadCount > 0 ? `${unreadCount} unread` : "You're up to date"}</p>
          </div>
          {unreadCount > 0 && onMarkAllRead && (
            <button type="button" className="ntf-mark" onClick={onMarkAllRead}>
              <i className="fas fa-check-double" aria-hidden="true" /> Mark all read
            </button>
          )}
        </div>

        {loading ? (
          <div className="ntf-center" role="status" aria-label="Loading notifications">
            <span className="ntf-spinner" />
          </div>
        ) : error ? (
          <div className="ntf-center" role="alert">
            <div className="ntf-empty-icon"><i className="fas fa-triangle-exclamation" aria-hidden="true" /></div>
            <h3>Couldn't load notifications</h3>
            <p>{error}</p>
            {onRetry && (
              <button type="button" className="ntf-retry" onClick={onRetry}>
                <i className="fas fa-rotate-right" aria-hidden="true" /> Try again
              </button>
            )}
          </div>
        ) : list.length === 0 ? (
          <div className="ntf-center">
            <div className="ntf-empty-icon"><i className="fas fa-bell" aria-hidden="true" /></div>
            <h3>You're all caught up</h3>
            <p>Updates about your requests, quotes and payments will appear here.</p>
          </div>
        ) : (
          <div>
            {list.map((n) => {
              const meta = TYPE_META[n.type] || defaultMeta;
              return (
                <button
                  key={n.id}
                  type="button"
                  className={`ntf-item${n.is_read ? "" : " unread"}`}
                  aria-label={`${n.title}${n.is_read ? "" : " (unread)"}`}
                  onClick={() => onNotificationClick && onNotificationClick(n)}
                >
                  <span className={`ntf-icon ${meta.tone}`} aria-hidden="true">
                    <i className={meta.icon} />
                  </span>
                  <span className="ntf-body">
                    <p className="ntf-item-title">{n.title}</p>
                    {n.message && <p className="ntf-item-msg">{n.message}</p>}
                    <p className="ntf-item-time">{when(n.created_at)}</p>
                  </span>
                  {!n.is_read && <span className="ntf-dot" aria-hidden="true" />}
                  <i className="fas fa-chevron-right ntf-chev" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
